<template>
    <div table question :id="`question-${questionData.number}`" @keyup.enter="submitQuestion">
        <questionheader :questionData="questionData" />
        <div
            data-question-main
            v-if="questionData.displayType !== `Matrix` || state.global.isMobile || this.isMobileView"
        >
            <div v-for="(sq, index) in questionData.subQuestions" :key="`sq-${index}`" data-answer>
                <div data-sq-label>
                    <label
                        data-answer-label
                        :for="`option-${sq.id}`"
                        v-html="DOMPurify.sanitize(sq.text)"
                        @keyup.space="selectedOptionIndex = index"
                        @keydown.space.prevent
                        @keydown.enter.stop.prevent="toggleShow(index)"
                        @keyup.enter.stop
                        @click="toggleShow(index)"
                        :data-sub-visible="index === selectedOptionIndex"
                        :ref="`subquestion`"
                        :tabindex="0"
                        role="button"
                        :aria-expanded="index === selectedOptionIndex"
                    ></label>
                    <answerdescription
                        v-if="sq.subQuestionDescription !== `` && sq.subQuestionDescription !== null"
                        :questionData="questionData"
                        :sq="sq"
                    />
                    <div
                        data-option-icon
                        :tabindex="index === selectedOptionIndex ? false : 0"
                        @keyup.space="selectedOptionIndex = index"
                        @keydown.space.prevent
                        @keydown.enter.stop.prevent="toggleShow(index)"
                        @keyup.enter.stop
                        :aria-label="index === selectedOptionIndex ? false : get.textResource(`rs_table_sq_icon_screenreader`)"
                        :id="`fold-${questionData.number}_sq-${sq.number}`"
                        :aria-expanded="index === selectedOptionIndex"
                        role="button"
                    >
                        <font-awesome-icon
                            icon="chevron-up"
                            :data-arrow-open="index !== selectedOptionIndex"
                            @click="toggleShow(index)"
                        />
                    </div>
                </div>
                <div data-answer-option :data-has-answers="hasAnswers(sq) && index !== selectedOptionIndex ? `` : false">
                    <div data-option-label @click="toggleShow(index)">
                        <div
                            v-for="(answer, index) in selectedAnswers(sq)"
                            :key="index"
                            data-selected-answer
                        >
                            <media
                                v-if="answerHasMedia(answer)"
                                :url="`${state.surveyInfo.domain}/v1/${answer.media[0].url}`"
                                :alt="answer.media[0].alt"
                                :type="answer.media[0].mediaType"
                            ></media>
                             <font-awesome-icon
                                data-icon
                                :icon="`check`"
                                aria-hidden="true"
                            />
                            <p
                                v-if="selectedAnswerHasLabel(answer)"
                                v-html="sanitizeAnswerLabel(answer)"
                            ></p>
                        </div>
                    </div>
                </div>
                <div
                    v-show="index === selectedOptionIndex"
                    data-option-list
                    :horizontal-list="questionData.displayType === `HorizontalAnswers` ? `` : false"
                >
                    <div
                        v-for="(option, optionIndex) in sq.answers"
                        :key="`table-option-${questionIndex}-${optionIndex}-${index}`"
                        data-option
                        :data-has-tooltip-and-image="answerHasMedia(option) && option.description !== `` ? `` : false"
                    >
                        <checkboxradio
                            v-if="option.type === `Radiobutton` || option.type === `Checkbox`"
                            :data-no-radio="answerHasMedia(option)"
                            :questionData="questionData"
                            :answerData="option"
                            :type="option.type === `Radiobutton` ? `radio` : `checkbox`"
                            :disabled="optionIsDisabled(option)"
                            :name="`option-${sq.id}`"
                            :sqData="sq"
                            :data-is-visible="index === selectedOptionIndex"
                            :data-answer-checked="option.isChecked"
                            :tabindex="answerHasMedia(option) ? -1 : 0"
                            :ref="`answer`"
                            @element-click="radiobuttonChanged($event, sq, optionIndex)"
                        />
                        <media
                            v-if="answerHasMedia(option)"
                            :url="`${state.surveyInfo.domain}/v1/${option.media[0].url}`"
                            :alt="option.media[0].alt"
                            :type="option.media[0].mediaType"
                            :disabled="optionIsDisabled(option)"
                            :tabindex="option.htmlInputType === `text` || option.htmlInputType === `number` || optionIsDisabled(option) ? -1 : 0"
                            @element-clicked="radiobuttonChanged($event, sq, optionIndex)"
                            @element-keyup="radiobuttonKeyDown($event, sq, optionIndex)"
                            role="option"
                            :id="`question-${questionData.number}_media-${sq.number}`"
                        />
                        <label
                            v-if="showLabel(option)"
                            :for="`question-${questionData.number}_sq-${sq.number}_checkradio-answer-${option.number}`"
                            data-option-text
                            v-html="DOMPurify.sanitize(questionData.subQuestions[0].answers[optionIndex].label)"
                        ></label>
                        <questioninput
                            v-if="option.htmlInputType === `text` || option.htmlInputType === `number`"
                            :answerData="option"
                            :showLabel="(answerHasMedia(option) && option.media[0].alignment !== `Over`) || answerHasMedia(option) === false"
                            :questionData="questionData"
                            :sqData="sq"
                            :ref="`sqinputs`"
                            :disabled="optionIsDisabled(option)"
                            :id="`question-${questionData.number}_input-${option.number}`"
                            @element-loses-focus="updateValuesOfInput(option)"
                            v-model="option.openField"
                        />
                        <answerdescription
                            v-if="option.description !== ``"
                            :answerData="option"
                            :questionData="questionData"
                            :sq="sq"
                        />
                    </div>
                    <div
                        v-if="state.global.nextButtonClicked === true &&
                        index === firstUnansweredSubQuestionIndex && questionIsValid === false"
                        data-subquestion-not-answered
                        :has-horizontal-answers="questionData.displayType === `HorizontalAnswers` ? `` : false"
                        :ref="`subquestionNotAnsweredValidation`"
                    >
                        <p data-subquestion-validation-message>
                            {{ validationText }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div
            data-question-main
            v-else-if="questionData.displayType === `Matrix` && !state.global.isMobile"
        >
            <table table-matrix>
                <thead>
                    <tr>
                        <th colspan="3"></th>
                        <th
                            v-for="(answer, index) in questionData.subQuestions[0].answers"
                            :key="`answer-${index}`"
                        >
                            <div answer-container>
                                <label
                                    v-if="selectedAnswerHasLabel(answer)"
                                    data-answer-text
                                    :aria-hidden="answerHasMedia(answer) ? true : false"
                                    v-html="DOMPurify.sanitize(answer.label)"
                                ></label>
                                <media
                                    v-if="answerHasMedia(answer)"
                                    :url="`${state.surveyInfo.domain}/v1/${answer.media[0].url}`"
                                    :alt="answer.media[0].alt"
                                    :type="answer.media[0].mediaType"
                                    :disabled="optionIsDisabled(answer)"
                                    :aria-label="answer.label"
                                    role="option"
                                />
                                <answerdescription
                                    v-if="answer.description !== ``"
                                    :answerData="answer"
                                    :questionData="questionData"
                                />
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(sq, index) in questionData.subQuestions"
                        :key="`sq-${index}`"
                        @click="selectedOptionIndex = index"
                    >
                        <td colspan="3">
                            <div data-sq-label>
                                <label
                                    data-answer-label
                                    :for="`option-${sq.id}`"
                                    v-html="DOMPurify.sanitize(sq.text)"
                                ></label>
                                <answerdescription
                                    v-if="sq.subQuestionDescription !== `` && sq.subQuestionDescription !== null"
                                    :questionData="questionData"
                                    :sq="sq"
                                />
                            </div>
                        </td>
                        <td
                            v-for="(option, optionIndex) in sq.answers"
                            :key="`table-option-${questionIndex}-${optionIndex}-${index}`"
                        >
                            <checkboxradio
                                v-if="option.type === `Radiobutton` || option.type === `Checkbox`"
                                :questionData="questionData"
                                :answerData="option"
                                :type="option.type === `Radiobutton` ? `radio` : `checkbox`"
                                :disabled="optionIsDisabled(option)"
                                :name="`option-${sq.id}`"
                                :sqData="sq"
                                :data-is-visible="index === selectedOptionIndex"
                                :data-answer-checked="option.isChecked"
                                :tabindex="0"
                                :ref="`answer`"
                                @element-click="radiobuttonChanged($event, sq, optionIndex)"
                            />
                            <questioninput
                                v-if="option.htmlInputType === `text` || option.htmlInputType === `number`"
                                :answerData="option"
                                :showLabel="false"
                                :sqData="sq"
                                :ref="`sqinputs`"
                                :questionData="questionData"
                                :disabled="optionIsDisabled(option)"
                                :id="`question-${questionData.number}_input-${option.number}`"
                                @element-receives-input="updateValuesOfInput(option)"
                                v-model="option.openField"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <questionfooter :questionData="questionData" />
        <validationpopup
            :questionValidation="questionValidation"
            :customValidation="customValidation"
        />
    </div>
</template>

<script>
import mixins from "@/global/mixins";

export default {
    name: `table-question`,
    inject: ['DOMPurify'],
    mixins: [mixins.defaultQuestion],
    data: function () {
        return {
            //selectedOptionIndex is the subquestion which is fold out and is set equal to subquestion index on click
            selectedOptionIndex: 0,
            isMobileView: false,
            validationText: this.$store.getters.textResource(`rs_question_is_required`)
        };
    },
    watch: {
        // set focus on the next row of radio buttons when the sub question folds
        selectedOptionIndex: function () {
            this.$nextTick(function () {
                let answersType = this.$refs.sqinputs ? this.$refs.sqinputs : this.$refs.answer;

                if (answersType && this.questionData.displayType !== `Matrix`) {
                    let visibleQuestions = this.$refs.subquestion.filter((subquestion) => {
                        return subquestion.attributes.getNamedItem(`data-sub-visible`) !== null;
                    });
                    let visibleRefs = answersType.filter((answer) => {
                        return answer.$el.attributes.getNamedItem(`data-is-visible`) !== null;
                    });

                    if (visibleQuestions.length > 0) visibleQuestions[0].focus();

                    setTimeout(() => {
                        if (visibleRefs.length > 0) visibleRefs[0].$el.childNodes[0].focus();
                    }, 0);
                }
            });
        },
    },
    computed: {
        firstUnansweredSubQuestionIndex() {
            if (!this.state.global.nextButtonClicked) return -1;

            return this.questionData.subQuestions.findIndex(
                sq => !this.hasCorrectAmountOfAnswers(sq)
            );
        },
        answersGiven() {
            if(this.questionData.closedAnswersRequired === true) {
                const correctlyFilledAnswers = this.questionData.subQuestions.filter((sq) => {
                    return sq.answers.find((answer) => {
                        return answer.isChecked && (answer.type === "Radiobutton" || answer.type === "Checkbox");
                    })
                }).length;

                return correctlyFilledAnswers === this.questionData.subQuestions.length ? 1 : 0;
            } else if (this.questionData.type === `TableSingle` || this.questionData.type === `TableMixed`) {
                return this.questionData.subQuestions.filter((sq) => {
                    return sq.answers.some((answer) => answer.isChecked);
                }).length;
            } else {
                let max = this.restrictions.maxValue;
                let min = this.restrictions.minValue;

                return this.questionData.subQuestions.filter((sq) => {
                    let checkedAmt = sq.answers.filter((answer) => answer.isChecked).length;

                    return (checkedAmt <= max || max === -1) && (checkedAmt >= min || min === -1);
                }).length;
            }
        },
        // This is needed to give the user visual clues about the min and max values that are allowed. The table questions are unique so the validation system doesn't handle it well othewrise
        customValidation() {
            let validationRules = [];

            if (this.questionData.type === `TableMulti`) {
                let answersPerSQ = this.questionData.subQuestions.map((sq) => sq.answers.filter((answer) => answer.isChecked).length);

                if (this.restrictions.maxValue !== -1) {
                    validationRules.push({
                        isValid: this.restrictions.maxValue >= Math.max(...answersPerSQ),
                        validationType: `maxValue`,
                        value: this.restrictions.maxValue,
                    });
                }
                if (this.restrictions.minValue !== -1) {
                    validationRules.push({
                        isValid: this.restrictions.minValue <= Math.min(...answersPerSQ),
                        validationType: `minValue`,
                        value: this.restrictions.minValue,
                    });
                }
            }

            return validationRules;
        },
    },
    methods: {
        sanitizeAnswerLabel(answer){
            const selectedAnswerLabel = this.questionData.subQuestions[0].answers.find(a => a.number === answer.number).label;
            return this.DOMPurify.sanitize(selectedAnswerLabel);
        },
        toggleShow: function (index) {
            let hasInputErrors;

            let subQuestionAnswers = this.$refs.sqinputs?.filter((sqinput) => sqinput.sqData.id === this.questionData.subQuestions[index].id);
            // Loop through the current subQuestion and find any invalid inputs which needs to corrected/filled in.
            subQuestionAnswers?.forEach((sq) => {
                const textField = sq.$el.querySelector(`input:not([type=checkbox]:not([type=radio]))`);
                if (textField.validationMessage !== ``) {
                    hasInputErrors = true;
                }
            });

            // If question is allready folded open and one or more inputs are not valid and you click next button, leave it open.
            if (this.selectedOptionIndex === index && hasInputErrors === true) {
                this.selectedOptionIndex = index;
            }
            // If clicking other subquestion to open rather than the one which is allready open, we add 1 to selectedOptionIndex to fold open the clicked subquestion
            // and fold the last open subquestion.
            else if (this.selectedOptionIndex === index) {
                this.selectedOptionIndex += 1;
            }
            // Don't update selectionOptionIndex if its the same subquestions being toggled.
            else {
                this.selectedOptionIndex = index;
            }
        },
        optionIsDisabled(option) {
            return option.isDisabled || !this.get.editingAllowed(this.questionData);
        },
        answerHasMedia(answer) {
            return answer.media.length > 0;
        },
        selectedAnswerHasLabel: function (answer) {
            let answerHasMedia = this.answerHasMedia(answer);

            if (answerHasMedia === true && answer.media[0].alignment === `Over`) return false;
            else return true;
        },
        showLabel(answer) {
            // Don't show a label when the image has to replace the label
            if (this.answerHasMedia(answer) && answer.media[0].alignment === `Over`) return false;
            // Don't show a label when we have a input text or input number. In that case the label is already done by questioninput instead
            else if (answer.htmlInputType === `text` || answer.htmlInputType === `number`) return false;
            else return true;
        },
        radiobuttonKeyDown(e, sq, optionIndex) {
            if (e.code !== `Space` && e.code !== `Enter`) return;

            e.preventDefault();
            e.stopPropagation();
            this.radiobuttonChanged(e, sq, optionIndex);
        },
        // Do things when radio button or selectbox changes it value
        radiobuttonChanged: function (e, sq, optionIndex) {
            let answer = sq.answers[optionIndex];
            let _this = this;

            // We don't want images with inputs clickable
            if ((e.target.nodeName === "IMG" && answer.htmlInputType === `text`) || answer.htmlInputType === `number`) return;

            // Radio buttons and v-model don't go along very well, so we do it manually
            if (sq.type === `TableSingle`) {
                answer.isChecked = true;

                sq.answers
                    .filter((a) => a.id !== answer.id)
                    .forEach((a) => {
                        a.isChecked = false;
                    });

                // This is needed because otherwise navigating radio buttons with the keyboard will trigger a click
                if (e.type !== "click" || (e.clientX !== 0 && e.clientY !== 0)) {
                    // For some reason the UI doesn't update without this timeout
                    setTimeout(() => {
                        _this.selectedOptionIndex++;
                    }, 0);
                }
            } else if (sq.type === `TableMixed` && answer.type === `Radiobutton`) {
                answer.isChecked = true;

                sq.answers
                    .filter((a) => a.id !== answer.id && a.type === `Radiobutton`)
                    .forEach((a) => {
                        a.isChecked = false;
                    });
            } else {
                answer.isChecked = !answer.isChecked;
            }

            // If an answer that is exclusive has been clicked, disable all remaining options
            if (answer.isExclusive) {
                sq.answers
                    .filter((a) => a.id !== answer.id)
                    .forEach((a) => {
                        if (answer.isChecked) {
                            a.isChecked = false;
                            a.isDisabled = true;
                        } else {
                            a.isDisabled = false;
                        }
                    });
            }
        },
        selectedAnswers: function (sq) {
            return sq.answers.filter((answer) => answer.isChecked);
        },
        hasCorrectAmountOfAnswers: function(sq) {
            let amountofAnswers = 0;
            let validationSettingsCount = this.customValidation.length;

            // this validation array with objects can have multiple but we want the first one (for example min and max where max is the first).
            let validation = this.customValidation[0];
            // Single doesnt has custom validation. Multi does.
            if(validation === undefined) {
                // Table plus has some standard minValue validation.
                let standardValidation = this.questionValidation;
                if(standardValidation !== undefined && standardValidation[0].validationType === `minValue`) {
                    this.validationText = this.$store.getters.textResource(`rs_table_question_validation_min`).replace(`{0}`, standardValidation[0].value);
                }
                else {
                    this.validationText = this.$store.getters.textResource(`rs_question_is_required`);
                }
              
                return sq.answers.filter((answer) => answer.isChecked).length > 0;
            }

            let validationType = validation.validationType;
            if(validationSettingsCount === 2 && this.customValidation[0].value === this.customValidation[1].value) {
                validationType = `minMaxValue`;
            }
            amountofAnswers = validation.value;

            this.validationText = this.$store.getters.textResource(`rs_question_is_required`);

			switch (validationType) {
				case `none`:
                    return sq.answers.filter((answer) => answer.isChecked).length > 0;
				case `minValue`:
                    this.validationText = this.$store.getters.textResource(`rs_table_question_validation_min`).replace(`{0}`, amountofAnswers);
                    return sq.answers.filter((answer) => answer.isChecked).length >= amountofAnswers;
				case `maxValue`:
                    this.validationText = this.$store.getters.textResource(`rs_table_question_validation_max`).replace(`{0}`, amountofAnswers);
                    return sq.answers.filter((answer) => answer.isChecked).length <= amountofAnswers;
                case `minMaxValue`:
                    this.validationText = this.$store.getters.textResource(`rs_table_question_validation_minmax`).replace(`{0}`, amountofAnswers).replace(`{1}`, amountofAnswers);
                    return sq.answers.filter((answer) => answer.isChecked).length == amountofAnswers;
			}           
        },
        hasAnswers: function(sq) {
            return sq.answers.filter((answer) => answer.isChecked).length > 0;
        },
        updateValuesOfInput: function (answer) {
            // We have to manually set the isChecked property of the input number and input text
            if (answer.htmlInputType === `text` || answer.htmlInputType === `number`) {
                if (answer.openField === ``) answer.isChecked = false;
                else answer.isChecked = true;
            }
        },
        onResize() {
            this.isMobileView = window.innerWidth < 500;
        },
    },
    created() {
        this.questionData.subQuestions.forEach((sq) => {
            sq.answers.forEach((answer) => {
                if (answer.isExclusive && answer.isChecked) {
                    sq.answers.filter((a) => a.id !== answer.id).forEach((a) => (a.isDisabled = true));
                }
            });
        });
    },
    mounted() {
        this.onResize();
        window.addEventListener("resize", this.onResize, { passive: true });
    },
};
</script>

<style scoped>
@import "./../styles/variables.css";
[data-answer] {
    flex-direction: column;
    align-items: flex-start;
    padding: 8px 0;
    margin-bottom: 8px;
    [data-option-list] {
        [data-option], [data-subquestion-not-answered] {
            &:last-of-type {
                color: var(--primary-color);
            }

            [data-subquestion-validation-message] {
                color: red;
                margin-block: 10px 0;
            }

            &[has-horizontal-answers] {
                clear: both;
                width: 100%;
                border: none;
                padding: 10px 0 0 0;
            }
        }

        &[horizontal-list] {
            border-color: var(--primary-color);
            border-bottom: 2px solid;
            color: var(--primary-color);
            padding-bottom: 20px;
            border-radius:0;
            [data-option]:last-of-type {
                padding: 0;
            }
        }
    }
    &:last-of-type {
        [data-option-list] {
            &[horizontal-list] {
                margin-bottom: 20px;
                border: none;
                [data-option]:last-of-type {
                    margin: 0;
                }
            }
        }
    }
}

[data-answer]:has([data-arrow-open]) {
    border-bottom: 1px solid #D9D9D9;
    &:last-of-type {
        border-bottom: 0;
    }
}

[data-sq-label] {
    display: flex;
    align-items: center;
    width: 100%;
}

[data-answer-option] {
    display: none;
    width: 100%;
    margin-bottom: 5px;
   
    &[data-has-answers] {
        display: flex;
    }
}

[data-selected-answer] {
    display: flex;
    padding: 5px 30px 5px 0;
    align-items: center;
    flex-shrink: 0;
    max-width: 250px;

    svg {
        margin-right: 8px;
    }

    p {
        white-space: nowrap;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 14px;
        margin: 0;
    }

    img {
        margin: 0;
        height: 40px;
        max-width: 180px;
        border-radius: 5px;
        object-fit: cover;
        margin-inline-end: 8px;
    }

    &:has(img) p {
        margin: 0;
    }
}

[data-answer-checked="true"][data-no-radio="true"] + img {
    outline: 1.31px solid var(--primary-color);
    box-shadow: 0 0 5px #000;
}

[data-option-text] {
    margin-left: 10px;
}

[data-option-label] {
    display: flex;
    width: 100%;
    margin-right: 15px;
    padding-left: 10px;
    padding-right: 10px;
    overflow: hidden;
    height: 50px;
    white-space: nowrap;
    cursor: pointer;
    text-overflow: ellipsis;
    /* border-bottom:2px solid #D9D9D9; */

    &:not(:empty) {
        transition: 0.3s;
        color: #454848;
        margin-bottom: 1px;
    }
}

[data-checkboxradio] {
    margin-left: 1px;
}

[data-arrow-open] {
    transform: rotate(180deg);
}

[data-answer-label] {
    margin-bottom: 5px;
    font-weight: bold;
    font-size: calc(var(--QuestionStyling-font-size) * 1px);
    width: 100%;
}

[data-option-icon] {
    width: 45px;
    height: 45px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: 0.5s ease-out;

    svg {
        transition: 0.4s;
        font-size: 21px;

        * {
            color: #333;
        }
    }
}

[data-option-list] {
    padding: 10px 0 15px 0;
    height: 100%;
    width: 100%;
    max-height: 100%;
    overflow-x: auto;
    transition: height 0.4s;
    border-bottom: 1px solid #D9D9D9;
    &:last-of-type {
        border-bottom: 0;
    }
}

[data-no-radio] {
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
}

[data-option] [data-questioninput] {
  padding: 16px 20px 16px 25px;
}

[data-option] {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    align-items: center;
    border-radius: 16px;
    border: 1px solid var(--border-black-color-25);
    box-sizing: border-box;

    [data-option-text] {
        padding: 18px 0;
        width: 100%;
    }

    img {
        margin-left: 2px;
        cursor: pointer;
        max-width: 80px;
        max-height: 80px;
        border-radius: 5px;

        ~ [data-questioninput] {
            margin-left: 15px;
        }

        &[tabindex="-1"] {
            outline: 0;
        }
    }

    &:not(:first-of-type) {
        margin-top: 8px;
    }

    &:not(:last-of-type) {
        margin-bottom: 8px;
    }

    &:has(> [data-answer-checked="true"]) {
        border: 1px solid var(--primary-color-25);
    }
    &:hover {
        background: #0000000D;
        border: 1px solid var(--border-black-color-35);
    }
}

[horizontal-list] {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-start;
    > [data-option] {
        width: auto;
        padding: 0 5px 0 5px;
        margin: 0 10px 10px 0;

       &[data-has-tooltip-and-image] {
            min-width: 200px;
            [data-tooltip] {
                margin: 0 25px 0 0;
            }
        }

        img {
            width: 100%;
        }
    }

    [data-option-text] {
        padding: 18px 18px 18px 0;
    }
}

/* Matrix styling */
[table-matrix] {
    border-collapse: collapse;
    margin: 0 0 5px 0;
    table-layout: fixed;
    width: 100%;

    th {
        text-align: center;
        [data-answer-text] {
            font-size: calc(var(--AnswerStyling-font-size) * 1px);
        }
    }

    tbody tr {
        border: 1px dashed white;
        td {
            padding: 5px;
            border-top: 1px solid #949494;
            border-bottom: 1px solid #949494;
        }

        [data-answer-label] {
            font-weight: normal;
            line-height: calc(var(--AnswerStyling-font-size) * 1px + 2);
        }
    }
}

[answer-container] {
    display: flex;
    justify-content: center;
    align-items: center;
    [data-tooltip] {
        margin-left: 5px;
    }
}
[data-tooltip] {
    align-items: center;
    margin: 0 25px;
}

[table-matrix] td [data-checkboxradio] {
    flex-direction: column;
}

@media only screen and (max-width: 550px) {
    [data-option-text] {
        margin-left: 20px;
    }
}
</style>
